import { StrictMode, useRef } from "react"
import { colors } from "design"
import { Global, css } from "@emotion/react"
import { useRoutes, BrowserRouter } from "react-router-dom"
import { lightTheme, Layout } from "widgets"
import { generateCSSVariables, useSlidePanel } from "utils"
import Header from "./base/header.js"
import Footer from "./base/footer.js"
import { useAppRoutes } from "./base/routes.js"
import { AppContextProvider } from "./base/app-context-provider.js"
import NavPanel from "../components/base/nav-panel.js"

const globalStyles = css`
  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-Thin.ttf") format("truetype");
    font-weight: 100;
    font-style: normal;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-ThinItalic.ttf") format("truetype");
    font-weight: 100;
    font-style: italic;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-ExtraLight.ttf") format("truetype");
    font-weight: 200;
    font-style: normal;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-ExtraLightItalic.ttf") format("truetype");
    font-weight: 200;
    font-style: italic;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-Light.ttf") format("truetype");
    font-weight: 300;
    font-style: normal;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-LightItalic.ttf") format("truetype");
    font-weight: 300;
    font-style: italic;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-Regular.ttf") format("truetype");
    font-weight: 400;
    font-style: normal;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-Italic.ttf") format("truetype");
    font-weight: 400;
    font-style: italic;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-Medium.ttf") format("truetype");
    font-weight: 500;
    font-style: normal;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-MediumItalic.ttf") format("truetype");
    font-weight: 500;
    font-style: italic;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-SemiBold.ttf") format("truetype");
    font-weight: 600;
    font-style: normal;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-SemiBoldItalic.ttf") format("truetype");
    font-weight: 600;
    font-style: italic;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-Bold.ttf") format("truetype");
    font-weight: 700;
    font-style: normal;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-BoldItalic.ttf") format("truetype");
    font-weight: 700;
    font-style: italic;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-ExtraBold.ttf") format("truetype");
    font-weight: 800;
    font-style: normal;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-ExtraBoldItalic.ttf") format("truetype");
    font-weight: 800;
    font-style: italic;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-Black.ttf") format("truetype");
    font-weight: 900;
    font-style: normal;
  }

  @font-face {
    font-family: "Poppins";
    src: url("/public/fonts/Poppins-BlackItalic.ttf") format("truetype");
    font-weight: 900;
    font-style: italic;
  }

  * {
    font-family: "Poppins";
    ${generateCSSVariables(lightTheme)};
    box-sizing: border-box;
    input::file-selector-button: {
      font-weight: bold;
      color: dodgerblue;
      padding: 0.5em;
      border: thin solid grey;
      border-radius: 3px;
    }
  }
  h1,
  h2 {
    margin: 0;
  }
  ,
  body {
    margin: 0;
  }
`

const Main = () => {
  const [showNavPanel, navPanel] = useSlidePanel(NavPanel)
  const appContentRef = useRef(null)
  const routes = useAppRoutes()
  const appRoutes = useRoutes(routes)

  return (
    <Layout
      className="app"
      css={{
        justifyContent: "space-between",
        minHeight: "100vh",
      }}
      vertical
    >
      <Global styles={globalStyles} />
      <Header
        onShowNavPanel={showNavPanel}
        onCloseNavPanel={navPanel?.onClose}
      />
      <div
        className="app-content"
        ref={appContentRef}
        css={{
          flexGrow: 1,
          width: "100%",
          display: "flex",
          position: "relative",
          background: colors.darkCream,
        }}
      >
        {navPanel}
        {appRoutes}
      </div>
      <Footer />
    </Layout>
  )
}

const App = () => (
  <StrictMode>
    <BrowserRouter>
      <AppContextProvider>
        <Main />
      </AppContextProvider>
    </BrowserRouter>
  </StrictMode>
)

export default App
